import React, { useEffect, useState } from "react";
import styles from "./BambooSetting.module.css"
import { Typography, Col, Row, Switch, Divider, Flex, Segmented, Button, Tooltip } from "antd";
import bambooLogo from '../../../assets/bamboo.svg';
import { UserOutlined, MessageOutlined, FileTextOutlined, TeamOutlined } from '@ant-design/icons';
import api from '../../../authentication/axiosInstance';
import { message } from 'antd';

const { Title, Text } = Typography;

export default function BambooSetting() {

    const [selected,setSelected] = useState([])
    const [syncSelected,setSyncSelected] = useState(null)

    const syncOptions = [
        { label: "Auto Sync", value: 'auto_sync' },
    ]
    const personalOptions = [ 
        { label: "First Name", value: 'sync_first_name' },
        { label: "Last Name", value: 'sync_last_name' },
        { label: "Name", value: 'sync_name' },
        { label: "Preferred Name", value: 'sync_pref_name' },
    ];
    
    const contactOptions = [
        { label: "Work Phone", value: 'sync_work_phone' },
        { label: "Work Phone Extension", value: 'sync_work_phone_extension' },
        { label: "Work Email", value: 'sync_work_email' },
        { label: "Home Email", value: 'sync_home_email' },
    ];
    
    const jobOptions = [
        { label: "Job Title", value: 'sync_job_title' },
        { label: "City", value: 'sync_city' },
        { label: "Pay Rate", value: 'sync_pay_rate' },
    ];
    
    const contractOptions = [
        { label: "Winter Contract Type", value: 'sync_winter_contract_type' },
        { label: "Winter Contract Weekly Standby", value: 'sync_winter_contract_weekly_standby' },
        { label: "Winter Contract Guaranteed Hours", value: 'sync_winter_contract_guaranteed_hours' },
        { label: "Winter Contract Hourly Rate", value: 'sync_winter_contract_hourly_rate' },
        { label: "Winter Contract Notes", value: 'sync_winter_contract_notes' },
        { label: "Winter Contract Part Time", value: 'sync_winter_contract_part_time' },
        { label: "Winter Contract Schedule", value: 'sync_winter_contract_schedule' }
    ];

    const fetchConfig = async () => {

        const fields = [
            ...syncOptions.map((option) => option.value),
            ...personalOptions.map((option) => option.value),
            ...contactOptions.map((option) => option.value),
            ...jobOptions.map((option) => option.value),
            ...contractOptions.map((option) => option.value)
        ].join(',');

        try {
            await api.get('/users/config/bamboo/', {
                params: {
                    fields
                }
            }).then((response) => {
                setSelected(response.data.filter(field => field.value === true).map(field => field.field))
                setSyncSelected(response.data.filter(field => field.field === 'auto_sync')[0].value)
            })
        } catch (error) {
            message.error('Error fetching configs: ', error)
        }
    }

    useEffect(() => {
        fetchConfig()
    },[])

    const handleToggle = (field,checked) => {
        if (checked) {
            if (!selected.includes(field.value)) {
                setSelected([...selected, field.value]); 
            }
        } else setSelected(selected.filter(item => item !== field.value)); 
        handleUpdateConfig(field.value,checked,false)
    }

    const handleUpdateConfig = async (field,newValue,toggleAll) => {

        let fields = []
        if (toggleAll) {
            fields = [
                ...personalOptions.map((option) => ({ 'field': option.value, 'new_value': true })),
                ...contactOptions.map((option) => ({ 'field': option.value, 'new_value': true })),
                ...jobOptions.map((option) => ({ 'field': option.value, 'new_value': true })),
                ...contractOptions.map((option) => ({ 'field': option.value, 'new_value': true }))
            ];
        } else fields = [{"field": field,"new_value": newValue}]
    
        try {
            await api.patch('users/config/bamboo/', {
                fields
            }).then(() => {
                fetchConfig()
            })
        } catch (error) {
            message.error('Error updating config', error)
        }
    }
    
    return (
    <Flex vertical justify="center" className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
            <Row align="middle" justify="space-between">
                <Col>
                    <Title level={2} className={styles.pageTitle}>
                        <img src={bambooLogo} className={styles.logo} alt="Bamboo" /> BambooHR Integration Settings
                    </Title>
                </Col>
            </Row>
        </div>

        <Flex vertical className={styles.settingContainer}>
            <Flex vertical align="flex-start" gap={6}>
                <Title level={4} className={styles.sectionTitle}>Sync Options</Title>
                <Flex justify="space-between" align="center" className={styles.sectionContainer}>
                    <Flex vertical align="flex-start" justify="flex-start">
                        <Text strong>Auto-Sync</Text>
                        <Text className={styles.subtitle}>Select how often you want the sync to occur</Text>
                    </Flex>
                    {syncSelected && 
                        <Segmented 
                            options={['Daily', 'Weekly', 'Monthly']}
                                onChange={(value) => {
                                    handleUpdateConfig('auto_sync',value,false)
                                }}
                            value={syncSelected.charAt(0).toUpperCase() + syncSelected.slice(1)}
                        />
                        }   
                </Flex>
            </Flex>
            <Divider/>
            <Flex vertical gap={6}>
                <Flex align="center" justify="space-between">
                    <Flex vertical align="flex-start" className={styles.sectionTitle}>
                        <Title level={4} className={styles.titleText}>General</Title>
                        <Text className={styles.subtitle}>Manage what fields you want to sync</Text>
                    </Flex>
                     <Tooltip title="Sync all data fields">
                        <Button
                            onClick={() => handleUpdateConfig(null,null,true)}
                            className={styles.selectAllButton}
                        >
                        Sync All
                        </Button>
                    </Tooltip>
                </Flex>
                <Flex gap={24} justify="space-between" >
                    <Flex className={styles.sectionContainer} style={{width: "25%"}} vertical gap={8}>
                        <Text className={styles.subSectionTitle} strong><UserOutlined className={styles.icon}/> Personal Information</Text>
                        {personalOptions.map(field => 
                            <Flex key={field.value} justify="space-between" align="center" className={styles.settingRow}>
                                <Text>{field.label}</Text>
                                    <Switch
                                        value={selected.includes(field.value)} 
                                        onChange={(checked) => handleToggle(field,checked)}
                                    />
                            </Flex>
                        )}
                    </Flex>
                    <Flex className={styles.sectionContainer} style={{width: "25%"}} vertical gap={8}>
                        <Text className={styles.subSectionTitle} strong><MessageOutlined className={styles.icon}/> Contact Information</Text>
                        {contactOptions.map(field => 
                            <Flex justify="space-between" align="center" className={styles.settingRow}>
                                <Text>{field.label}</Text>
                                    <Switch
                                        value={selected.includes(field.value)} 
                                        onChange={(checked) => handleToggle(field,checked)}
                                    />
                            </Flex>
                        )}
                    </Flex>
                    <Flex className={styles.sectionContainer} style={{width: "25%"}} vertical gap={8}>
                        <Text className={styles.subSectionTitle} strong><TeamOutlined className={styles.icon}/> Employment Information</Text>
                        {jobOptions.map(field => 
                            <Flex justify="space-between" align="center" className={styles.settingRow}>
                                <Text>{field.label}</Text>
                                    <Switch
                                        value={selected.includes(field.value)} 
                                        onChange={(checked) => handleToggle(field,checked)}
                                    />
                            </Flex>
                        )}
                    </Flex>
                    <Flex className={styles.sectionContainer} style={{width: "25%"}} vertical gap={8}>
                        <Text className={styles.subSectionTitle} strong><FileTextOutlined className={styles.icon}/> Contract Information</Text>
                        {contractOptions.map(field => 
                            <Flex justify="space-between" align="center" className={styles.settingRow}>
                                <Text>{field.label}</Text>
                                    <Switch
                                        value={selected.includes(field.value)} 
                                        onChange={(checked) => handleToggle(field,checked)}
                                    />
                            </Flex>
                        )}
                    </Flex>
                </Flex>
              
            </Flex>
        </Flex>
    </Flex>
    )
}